import _ from "lodash";
import PropTypes from "prop-types";
import React, { memo, useContext } from "react";
import { createUseStyles } from "react-jss";
import { StyleTypeContext, TranslationContext } from "../../../umbilical_tools";
import { InlineIcon } from "@iconify/react";
import jssVariables from "../../../jss_setup";
import ReactGA4 from "react-ga4";
import { Placeholder } from "rsuite";
import { Price } from "../../../components/common/Price";
import { Button } from "../../../components/common/Button";
import hotelIconMobileType2 from "../../../components/icons/hotelIconMobileType2.svg";
import { useSelector } from "react-redux";
import { hotelsIsSearchingSelector } from "../../../umbilical_hocs/hotels_selector";
import { useLiveCurrentPoints } from "../../../hooks/points";

export function onViewHotelDetails({
  history,
  hotelId,
  search_uid,
  page_session_id,
}) {
  history.push({
    pathname: `/hotel_details/${page_session_id}`,
    search: `?hotelId=${hotelId}&search_uid=${search_uid}`,
  });
}

export function onViewHotelDetailsNewTab({
  hotelId,
  search_uid,
  page_session_id,
}) {
  const search = `?hotelId=${hotelId}&search_uid=${search_uid}`;
  const pathname = `${window.location.origin}/hotel_details/${page_session_id}${search}`;

  const newWindow = window.open(pathname, "_blank", "noopener,noreferrer");
  if (newWindow) newWindow.opener = null;
}

const hotelOverlayStyles = createUseStyles({
  hotelOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    opacity: 100,
    zIndex: 1,
  },
  [`@media ${jssVariables.media.smallscreen}`]: {
    hotelOverlay: {
      display: "none",
    },
  },
});
export var HotelOverlay = ({ onClick }) => {
  const classes = hotelOverlayStyles();
  return <div className={classes.hotelOverlay} onClick={onClick} />;
};
HotelOverlay.propTypes = {
  onClick: PropTypes.func.isRequired,
};
HotelOverlay = memo(HotelOverlay);

const addressStyles = createUseStyles({
  Address: (props) => ({
    display: "grid",
    gridTemplateColumns: "max-content 1fr",
    gridGap: jssVariables.half_gap,
    width: "100%",
    fontSize: "medium",
    paddingTop: jssVariables.half_gap,
    ..._.get(props, "customStyles.Address"),
  }),
  mapIcon: (props) => ({
    fontSize: "larger",
    color: jssVariables.colors.base_color,
    justifySelf: "center",
    marginLeft: `calc(${jssVariables.normal_gap} / 5)`,
    cursor: "pointer",
    ..._.get(props, "customStyles.mapIcon"),
  }),
  addressText: (props) => ({
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "block",
    width: "100%",
    ..._.get(props, "customStyles.addressText"),
  }),
  distanceFromSelection: (props) => ({
    ..._.get(props, "customStyles.distanceFromSelection"),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    Address: (props) => ({
      gridTemplateColumns: "1.4rem 1fr",
      alignItems: "center",
      paddingTop: jssVariables.half_gap,
      ..._.get(props, "customStyles.Address.smallscreen"),
    }),
    mapIcon: (props) => ({
      fontSize: "x-large",
      ..._.get(props, "customStyles.mapIcon.smallscreen"),
    }),
  },
  [`@media ${jssVariables.media.bigscreen}`]: {},
});
export var Address = ({ address, showHotelOnMap, distanceFromSelection }) => {
  const { type, brand, cStyler } = useContext(StyleTypeContext);

  const classes = addressStyles({
    customStyles: cStyler({
      type,
      brand,
      objName: "hotelAddressStyles",
    }),
  });
  const { t } = useContext(TranslationContext);

  return (
    <React.Fragment>
      <div className={classes.Address}>
        <InlineIcon
          icon="majesticons:map-marker"
          className={classes.mapIcon}
          onClick={showHotelOnMap ? showHotelOnMap : null}
        />
        <span className={classes.addressText}>
          {_.words(_.toLower(address), /[^, ]+/g)
            .map((word) => _.capitalize(word))
            .join(" ")}
        </span>
        {distanceFromSelection > 0 && (
          <React.Fragment>
            <span></span>
            <span className={classes.distanceFromSelection}>
              {t("distanceFromSelection")}
              {": "}
              {(Math.round(distanceFromSelection * 100) / 100).toFixed(2)}{" "}
              {t("km")}
            </span>
          </React.Fragment>
        )}
      </div>
    </React.Fragment>
  );
};
Address.defaultProps = {
  distanceFromSelection: 0,
};
Address.propTypes = {
  showHotelOnMap: PropTypes.func,
  distanceFromSelection: PropTypes.number,
  address: PropTypes.string.isRequired,
};
Address = memo(Address);

function getPriceStyles(customStyles) {
  return {
    priceComponent: {
      fontWeight: "bold",
      color: jssVariables.colors.base_color,
      fontSize: "x-large",
      lineHeight: "normal",
      ..._.get(customStyles, "Price", {}),
    },
  };
}

const baseStyles = {
  BottomContainer: (props) => ({
    display: "grid",
    gridTemplateColumns: props.withPrice ? "1fr 1fr" : "1fr",
    alignItems: "end",
    alignSelf: "end",
    height: "max-content",
    padding: `calc(${jssVariables.normal_gap} * 1.5)`,
    ..._.get(props, "customStyles.BottomContainer", {}),
  }),
  divider: (props) => ({
    display: "none",
    marginLeft: `calc(${jssVariables.normal_gap} * 1.5)`,
    alignSelf: "center",
    width: "3rem",
    height: jssVariables.half_gap,
    background: jssVariables.colors.background.dark,
    gridColumn: props.ghost ? "span 2" : "unset",
    ..._.get(props, "customStyles.divider", {}),
  }),
  placeholderParagraph: (props) => ({
    width: "100%",
    gridColumn: "span 2",
    ..._.get(props, "customStyles.placeholderParagraph", {}),
  }),
  cxlContainer: (props) => ({
    ..._.get(props, "customStyles.cxlContainer", {}),
  }),
  priceContainer: (props) => ({
    display: "grid",
    fontSize: "small",
    width: "max-content",
    ..._.get(props, "customStyles.priceContainer", {}),
  }),
  pricePerNightTxt: (props) => ({
    ..._.get(props, "customStyles.pricePerNightTxt", {}),
  }),
  priceTxt: (props) => ({
    display: "grid",
    gridTemplateColumns: "max-content max-content",
    gridGap: jssVariables.half_gap,
    alignItems: "baseline",
    justifyContent: "space-between",
    color: jssVariables.colors.base_color,
    ..._.get(props, "customStyles.priceTxt", {}),
  }),
  nightsAndBoardTxt: (props) => ({
    fontSize: "large",
    ..._.get(props, "customStyles.nightsAndBoardTxt", {}),
  }),
  bottomImgContr: (props) => ({
    display: "grid",
    gridTemplateColumns: "max-content max-content",
    alignItems: "center",
    justifyContent: "end",
    gridGap: jssVariables.normal_gap,
    fontSize: "medium",
    fontWeight: "bold",
    color: jssVariables.colors.text.light,
    cursor: "pointer",
    ..._.get(props, "customStyles.bottomImgContr", {}),
  }),
};
const bottomContainerStyles = createUseStyles({
  ...baseStyles,
  [`@media ${jssVariables.media.smallscreen}`]: {
    BottomContainer: (props) => ({
      gridTemplateColumns: "1fr",
      gridTemplateRows: "max-content max-content",
      alignContent: "end",
      justifyItems: "end",
      gridGap: jssVariables.normal_gap,
      minWidth: "100%",
      ..._.get(props, "customStyles.BottomContainer.smallscreen", {}),
    }),
    divider: (props) => ({
      display: "block",
      ..._.get(props, "customStyles.divider.smallscreen", {}),
    }),
    placeholderParagraph: (props) => ({
      justifySelf: "start",
      ..._.get(props, "customStyles.placeholderParagraph.smallscreen", {}),
    }),
    priceContainer: (props) => ({
      ..._.get(props, "customStyles.priceContainer.smallscreen", {}),
    }),
    pricePerNightTxt: (props) => ({
      ..._.get(props, "customStyles.pricePerNightTxt.smallscreen", {}),
    }),
    priceTxt: (props) => ({
      ..._.get(props, "customStyles.priceTxt.smallscreen", {}),
    }),
  },
});
const mobileViewForDesktopStyles = createUseStyles({
  ...baseStyles,
  [`@media ${jssVariables.media.smallscreen}`]: {
    BottomContainer: (props) => ({
      gridTemplateColumns: "max-content",
      gridTemplateRows: "1fr",
      justifyItems: "end",
      ..._.get(props, "customStyles.BottomContainer.smallscreen", {}),
    }),
    divider: (props) => ({
      ..._.get(props, "customStyles.divider.smallscreen", {}),
    }),
  },
  [`@media ${jssVariables.media.bigscreen}`]: {},
});
export var BottomContainer = ({
  idx,
  ghost,
  mobileViewOnly,
  hotel,
  withPrice,
  page_session_id,
  onViewHtlSearchForm,
}) => {
  const { t } = useContext(TranslationContext);

  const { type, brand, cStyler } = useContext(StyleTypeContext);

  const customStyles = cStyler({
    type,
    brand,
    withPrice,
    objName: mobileViewOnly
      ? "hotelBottomContainerMobileView"
      : "hotelBottomContainer",
  });

  const classes = mobileViewOnly
    ? mobileViewForDesktopStyles({
        ghost,
        customStyles,
      })
    : bottomContainerStyles({
        ghost,
        customStyles,
      });

  const { isSearching } = useSelector((state) => {
    return { isSearching: hotelsIsSearchingSelector(state) };
  });

  const { hotelId, search_uid, stay, firstRoom, min_price, currency } = {
    hotelId: _.get(hotel, "metadata.id", "").toString(),
    search_uid: _.get(hotel, "uid", "").toString(),
    stay: _.get(hotel, "computed_data.stay", 0),
    firstRoom: _.get(hotel, "rooms.0", {}),
    min_price: _.get(hotel, "computed_data.min_price", 0),
    currency: _.get(hotel, "computed_data.currency", ""),
  };

  function onView() {
    onViewHotelDetailsNewTab({ hotelId, search_uid, page_session_id });
    ReactGA4.event({
      category: "action",
      action: "See_hotel_details",
      label: "See_hotel_details",
    });
  }
  var viewingFn = onView;
  if (typeof onViewHtlSearchForm === "function") {
    viewingFn = () => onViewHtlSearchForm({ hotelId });
  }

  const room = ["type3", "type4"].includes(type) ? firstRoom : {};
  const nightsTerm = t(stay > 1 ? "nights" : "night");

  return (
    <React.Fragment>
      {["type3"].includes(type) ||
        ([
          "atlantic",
          "festival",
          "infinite",
          "knoxtravel",
          "traveldealfinders",
        ].includes(brand) && <div className={classes.divider} />)}
      <div className={classes.BottomContainer}>
        {ghost ? (
          <Placeholder.Paragraph
            rows={2}
            active
            className={classes.placeholderParagraph}
          />
        ) : (
          <React.Fragment>
            {withPrice && (
              <div className={classes.priceContainer}>
                {["type1"].includes(type) && (
                  <span className={classes.pricePerNightTxt}>
                    <span>{_.capitalize(t("priceFor"))} </span>
                    <strong>{`${stay} ${nightsTerm}`}</strong>
                  </span>
                )}
                {type === "type3" && <span>{room.name}</span>}
                {type === "type2" && (
                  <span className={classes.pricePerNightTxt}>{`${t(
                    "priceFor"
                  )} ${stay} ${nightsTerm}`}</span>
                )}
                {type === "type3" && (
                  <span>{`${stay} ${nightsTerm} ${room.board_display}`}</span>
                )}
                <span className={classes.priceTxt}>
                  {["type1", "type2", "type4"].includes(type)
                    ? `${_.capitalize(t("from"))} `
                    : type === "type3"
                    ? `${t("totalPrice")} `
                    : null}
                  <Price
                    id={`accPrice${idx + 1}`}
                    price={min_price}
                    currency={currency}
                    customStyles={getPriceStyles(customStyles)}
                  />
                </span>
                {type === "type4" && (
                  <span className={classes.nightsAndBoardTxt}>
                    {stay} {` ${stay > 1 ? t("nights") : t("night")} `}
                    {room.board_display}
                  </span>
                )}
              </div>
            )}
            {["type1", "type3", "type4"].includes(type) && !isSearching ? (
              <Button
                id={`seeAvailabilityBtn${idx + 1}`}
                showIcon={brand === "rocs" ? true : false}
                icon={
                  brand === "rocs"
                    ? "akar-icons:arrow-right"
                    : "icons8:angle-right"
                }
                size={brand === "rocs" ? "x-large" : "medium"}
                bold={brand === "thehotelco" ? true : false}
                text={brand === "rocs" ? "" : t("seeAvailability")}
                empty={brand === "infinite" ? true : false}
                revert={brand === "infinite" ? true : false}
                onClick={viewingFn}
                customStyles={customStyles?.buttonStylesHtlBtmContr ?? {}}
              />
            ) : null}
            {type === "type2" && !isSearching && (
              <span className={classes.bottomImgContr} onClick={viewingFn}>
                <img src={hotelIconMobileType2} alt="" />
                {type === "type2" && "VIEW"}
              </span>
            )}
          </React.Fragment>
        )}
      </div>
    </React.Fragment>
  );
};
BottomContainer.defaultProps = {
  ghost: false,
  mobileViewOnly: false,
  hotel: {},
  withPrice: true,
  page_session_id: "",
  idx: 0,
};
BottomContainer.propTypes = {
  ghost: PropTypes.bool,
  mobileViewOnly: PropTypes.bool,
  hotel: PropTypes.object,
  withPrice: PropTypes.bool,
  page_session_id: PropTypes.string,
  onViewHtlSearchForm: PropTypes.func,
  idx: PropTypes.number,
};
BottomContainer = memo(BottomContainer);

export function checkIfImageExists(url, callback) {
  const img = new Image();
  img.src = url;

  if (img.complete) {
    callback(true);
  } else {
    img.onload = () => {
      callback(true);
    };

    img.onerror = () => {
      callback(false);
    };
  }
}
